const STORAGE_KEYS = {
  accessToken: 'mc_admin_token',
  refreshToken: 'mc_admin_refresh_token',
  lastActivity: 'mc_admin_last_activity'
};

const TIMEOUTS = {
  inactivityWarning: 25 * 60 * 1000,
  inactivityLogout: 30 * 60 * 1000,
  tokenRefreshBuffer: 2 * 60 * 1000,
};

let inactivityTimer = null;
let warningTimer = null;
let onWarningCallback = null;
let onLogoutCallback = null;

export const parseJwt = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
};

export const isTokenExpiringSoon = () => {
  const token = localStorage.getItem(STORAGE_KEYS.accessToken);
  if (!token) return true;
  const payload = parseJwt(token);
  if (!payload || !payload.exp) return true;
  const expiresAt = payload.exp * 1000;
  return (expiresAt - Date.now()) < TIMEOUTS.tokenRefreshBuffer;
};

export const isTokenExpired = () => {
  const token = localStorage.getItem(STORAGE_KEYS.accessToken);
  if (!token) return true;
  const payload = parseJwt(token);
  if (!payload || !payload.exp) return true;
  return Date.now() >= payload.exp * 1000;
};

export const storeTokens = (accessToken, refreshToken) => {
  localStorage.setItem(STORAGE_KEYS.accessToken, accessToken);
  if (refreshToken) localStorage.setItem(STORAGE_KEYS.refreshToken, refreshToken);
  updateLastActivity();
};

export const getRefreshToken = () => localStorage.getItem(STORAGE_KEYS.refreshToken);

export const clearAuth = () => {
  localStorage.removeItem(STORAGE_KEYS.accessToken);
  localStorage.removeItem(STORAGE_KEYS.refreshToken);
  localStorage.removeItem(STORAGE_KEYS.lastActivity);
  stopInactivityTracking();
};

export const updateLastActivity = () => {
  localStorage.setItem(STORAGE_KEYS.lastActivity, Date.now().toString());
  resetInactivityTimer();
};

const resetInactivityTimer = () => {
  if (warningTimer) clearTimeout(warningTimer);
  if (inactivityTimer) clearTimeout(inactivityTimer);
  warningTimer = setTimeout(() => { if (onWarningCallback) onWarningCallback(); }, TIMEOUTS.inactivityWarning);
  inactivityTimer = setTimeout(() => { if (onLogoutCallback) onLogoutCallback(); }, TIMEOUTS.inactivityLogout);
};

export const startInactivityTracking = (onWarning, onLogout) => {
  onWarningCallback = onWarning;
  onLogoutCallback = onLogout;
  const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
  const handleActivity = () => updateLastActivity();
  events.forEach(e => window.addEventListener(e, handleActivity, { passive: true }));
  updateLastActivity();
  return () => {
    events.forEach(e => window.removeEventListener(e, handleActivity));
    stopInactivityTracking();
  };
};

export const stopInactivityTracking = () => {
  if (warningTimer) clearTimeout(warningTimer);
  if (inactivityTimer) clearTimeout(inactivityTimer);
  warningTimer = null;
  inactivityTimer = null;
};

export const extendSession = () => updateLastActivity();

// @s11-idle-enforced-at-boot (9 Oct 2026)
// The 30-minute idle logout was enforced only by setTimeout timers, which exist
// only while a tab is open. Close the browser and come back days later and the
// boot check in App.jsx found a stale access token, silently refreshed it with
// the 30-day refresh token, and signed the admin straight back in - so the real
// session window was up to 30 days, not 30 minutes.
//
// lastActivity was already being written on every interaction and cleared on
// logout; nothing ever read it at start-up. This is that missing read.
//
// ORDER MATTERS: startInactivityTracking() calls updateLastActivity() as its
// first act, which overwrites the stored timestamp with now. So this must be
// called BEFORE tracking starts, or it can only ever return false. In App.jsx
// the boot check runs inside the Protected gate before the authenticated
// layout that starts tracking is mounted - keep it that way.
//
// Returns false when no timestamp is stored: a fresh login has not written one
// yet, and locking someone out of a session they just started would be worse
// than the gap this closes.
export const isIdleExpired = () => {
  const raw = localStorage.getItem(STORAGE_KEYS.lastActivity);
  if (!raw) return false;
  const last = parseInt(raw, 10);
  if (!Number.isFinite(last)) return false;
  return Date.now() - last > TIMEOUTS.inactivityLogout;
};
